import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useGeoData } from '../../hooks/geoData/useGeoData';
import {
    getSiteLocaleFromPathname,
    scrollIntoViewIfNeeded,
} from '../../utils/helpers';
import {
    CompleteRegisterPayload,
    CountryOptionType,
    OptionType,
} from '../../utils/types';
import {
    Title,
    TextField,
    TextInput,
    Button,
    GenericError,
    Autocomplete,
    CountrySelect,
} from '../common';
import { completeUserProfile } from '../../api/auth';
import { useTranslation } from '../../hooks/translations/TranslationProvider';
import useSeo from '../../hooks/useSeo';
import Checkbox from '../common/Checkbox/Checkbox';
import { getCurrentUser } from '../../store/auth/authSlice';
import { LANGUAGES } from '../../utils/consts';
import UsernameField from './UsernameField';

const initialFormValues = {
    username: '',
    name: '',
    surname: '',
    country: '',
    city: '',
    acceptTerms: false,
};

interface FormValues {
    username: string;
    name?: string;
    surname?: string;
    country: string;
    city: string;
    acceptTerms: boolean;
}

interface Errors {
    username: string | null;
    name: string | null;
    surname: string | null;
    country: string | null;
    city: string | null;
    acceptTerms: string | null;
}

const Register = () => {
    const auth = useAppSelector(state => state.auth);
    const dispatch = useAppDispatch();
    const navigate = useLocalizedNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const locale = getSiteLocaleFromPathname(location.pathname);

    useSeo({
        title: t('seo.register.title'),
        description: t('seo.register.description'),
        path: '/register',
        noIndex: true,
    });

    const {
        isLoading: isGeoDataLoading,
        error: geoDataError,
        countriesData,
        citiesData,
        getCities,
    } = useGeoData();

    const [countryCode, setCountryCode] = useState(
        locale === 'pl' ? 'PL' : 'GB'
    );
    const [formValues, setFormValues] = useState<FormValues>({
        ...initialFormValues,
        country: locale === 'pl' ? 'PL' : 'GB',
    });
    const [errors, setErrors] = useState<Errors>({
        ...initialFormValues,
        acceptTerms: null,
    });
    const [cityId, setCityId] = useState('');
    const [error, setError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const missingFromState = (location.state as { missing?: string[] } | null)
        ?.missing;

    const missing: string[] =
        missingFromState ||
        JSON.parse(sessionStorage.getItem('missingProfileFields') || '[]');

    useEffect(() => {
        if (auth.authData) {
            navigate('/home', { replace: true });
        }
    }, [auth, navigate]);

    const inputChangeHandler = (name: string, value: string) => {
        setFormValues({
            ...formValues,
            [name]: value,
        });
        setErrors({
            ...initialFormValues,
            acceptTerms: null,
        });
    };

    const onSubmitHandler = async (event: React.FormEvent) => {
        event.preventDefault();

        let hasErrors = false;
        const formErrors = { ...errors };
        let key: keyof FormValues;
        let firstInputToFocus: HTMLElement | null = null;
        if (!missing.includes('name')) {
            delete formValues.name;
        }

        if (!missing.includes('surname')) {
            delete formValues.surname;
        }

        for (key in formValues) {
            if (!formValues[key]) {
                hasErrors = true;
                formErrors[key] = t('errors.requiredField');
                if (!firstInputToFocus) {
                    firstInputToFocus = document.getElementsByName(key)[0];
                }
            }
        }

        if (hasErrors) {
            setErrors({
                ...formErrors,
            });
            scrollIntoViewIfNeeded(firstInputToFocus);
            firstInputToFocus?.focus();
            return;
        }

        const payload: CompleteRegisterPayload = {
            ...formValues,
            countryCode,
            city: cityId,
            language: locale.toUpperCase() as LANGUAGES,
        };

        try {
            setIsLoading(true);
            await completeUserProfile(payload);
            await dispatch(getCurrentUser());
            navigate('/home', { replace: true });

            setIsLoading(false);
        } catch (e: any) {
            if (e.response.status === 400) {
                setErrors({
                    ...formErrors,
                    [e.response.data.errors[0].path]:
                        e.response.data.errors[0].msg,
                });
            }
            if (e.response.status === 500) {
                setError(true);
            }
            setIsLoading(false);
        }
    };

    const getCitiesHandler = async (initials: string) => {
        await getCities(countryCode, initials);
    };

    const generateOptions = useMemo(() => {
        const citiesOptions: OptionType[] = [];

        citiesData?.forEach(city => {
            citiesOptions.push({
                id: city.id,
                label: city.name,
            });
        });
        return { citiesOptions };
    }, [countriesData, citiesData]);

    const onCountryChange = (fieldName: string, result: CountryOptionType) => {
        setCountryCode(result.id as string);

        setFormValues({
            ...formValues,
            city: '',
            [fieldName]: result.id,
        });

        setErrors({
            ...errors,
            country: null,
            city: null,
        });
    };

    const onCityChange = (fieldName: string, result: OptionType) => {
        setCityId(result.id as string);
        setFormValues({
            ...formValues,
            [fieldName]: result.label,
        });

        setErrors({
            ...errors,
            country: null,
            city: null,
        });
    };

    function onCheckboxChange(fieldName: string, checked: boolean) {
        setFormValues({
            ...formValues,
            acceptTerms: checked,
        });
        setErrors({
            ...errors,
            acceptTerms: null,
        });
    }

    function usernameValidationHandler(hasError: boolean) {
        setErrors(prev => ({
            ...prev,
            username: hasError ? 'Username is required' : null,
        }));
    }

    if (geoDataError || error) {
        return <GenericError error={error} />;
    }

    return (
        <main className="register-main page-container">
            <div className="register-title">
                <Title text={t('completeRegistration.title')} size="xxl" />
                <TextField size="l" color="subtitle">
                    {t('completeRegistration.subtitle')}
                </TextField>
            </div>
            <form onSubmit={onSubmitHandler} className="register-form">
                <UsernameField
                    value={formValues.username}
                    onValueChange={value =>
                        setFormValues(prev => ({ ...prev, username: value }))
                    }
                    onValidationChange={usernameValidationHandler}
                    formError={errors.username}
                />
                <div className="register-double-input">
                    {missing.includes('name') && (
                        <TextInput
                            label={t('register.name')}
                            name="name"
                            placeholder={t('register.name')}
                            onChange={inputChangeHandler}
                            value={formValues.name!}
                            error={errors.name}
                        />
                    )}
                    {missing.includes('surname') && (
                        <TextInput
                            label={t('register.surname')}
                            name="surname"
                            placeholder={t('register.surname')}
                            onChange={inputChangeHandler}
                            value={formValues.surname!}
                            error={errors.surname}
                        />
                    )}
                </div>
                <div className="register-country-city">
                    <TextField weight="bold">
                        {t('register.location')}
                    </TextField>
                    <div>
                        <CountrySelect
                            name="country"
                            onSelect={onCountryChange}
                            value={formValues.country}
                        />
                        <Autocomplete
                            name="city"
                            startSearch={getCitiesHandler}
                            isLoading={isGeoDataLoading}
                            error={geoDataError}
                            formError={errors.city}
                            results={generateOptions.citiesOptions}
                            handleOptionClick={onCityChange}
                            placeholder="Search cities"
                            initialValue={(formValues.city as string) || ''}
                            className="full-width"
                        />
                    </div>
                </div>

                <div className="register-accept-terms">
                    <Checkbox
                        name="acceptTerms"
                        isChecked={formValues.acceptTerms}
                        onChange={onCheckboxChange}
                        label={t('register.acceptTerms')}
                        error={errors.acceptTerms}
                    />
                </div>
                <Button
                    type="submit"
                    text={t('completeRegistration.submit')}
                    variant="green"
                    disabled={isLoading}
                />
            </form>
        </main>
    );
};

export default Register;
